import React from 'react';
import { Palette, User, Leaf, Target } from 'phosphor-react';
import { SkinProfile } from '../../types/entities';

const SKIN_TYPE_LABELS: Record<string, string> = {
  OILY: 'Oleosa',
  DRY: 'Seca',
  COMBINATION: 'Mista',
  NORMAL: 'Normal',
};

const SENSITIVITY_LABELS: Record<string, string> = {
  LOW: 'Baixa',
  MEDIUM: 'Média',
  HIGH: 'Alta',
};

interface StatColProps {
  icon: React.ReactNode;
  label: string;
  value: string;
}

function StatCol({ icon, label, value }: StatColProps) {
  return (
    <div className="px-3 first:pl-0 last:pr-0">
      <div className="flex items-center gap-1.5 mb-1">
        {icon}
        <p className="text-[11px] text-nude-400 dark:text-gray-400">{label}</p>
      </div>
      <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">{value}</p>
    </div>
  );
}

interface SkinProfileSectionProps {
  userName: string | null | undefined;
  skinProfile: SkinProfile | null;
  onGoToSkinProfile: () => void;
}

/** Card de perfil do mock (2026-06-11): avatar + nome + resumo da pele em 3 colunas. */
export default function SkinProfileSection({ userName, skinProfile, onGoToSkinProfile }: SkinProfileSectionProps) {
  if (!skinProfile) {
    return (
      <button
        onClick={onGoToSkinProfile}
        className="bg-lavender-50 dark:bg-lavender-900/20 border-2 border-dashed border-lavender-200 dark:border-lavender-800 rounded-2xl p-6 text-center hover:border-lavender-400 transition-colors"
      >
        <Palette className="w-8 h-8 text-lavender-400 mx-auto mb-2" />
        <p className="text-sm font-semibold text-lavender-700 dark:text-lavender-300">
          Preencher perfil de pele
        </p>
        <p className="text-xs text-lavender-400 mt-1">
          Toque para fazer a anamnese
        </p>
      </button>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-soft dark:shadow-none dark:border dark:border-gray-700">
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 flex items-center justify-center rounded-full bg-blush-100 dark:bg-gray-700 text-blush-600 dark:text-gray-100 text-2xl font-bold">
          {userName?.charAt(0).toUpperCase() || '?'}
        </div>
        <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">{userName}</h3>
      </div>

      <div className="grid grid-cols-3 divide-x divide-nude-100 dark:divide-gray-700 mt-4">
        <StatCol
          icon={<User className="w-3.5 h-3.5 text-blush-400" />}
          label="Tipo de pele"
          value={SKIN_TYPE_LABELS[skinProfile.skinType] || skinProfile.skinType}
        />
        <StatCol
          icon={<Leaf className="w-3.5 h-3.5 text-sage-400" />}
          label="Sensibilidade"
          value={SENSITIVITY_LABELS[skinProfile.sensitivityLevel] || skinProfile.sensitivityLevel}
        />
        <StatCol
          icon={<Target className="w-3.5 h-3.5 text-blush-400" />}
          label="Objetivo"
          value={skinProfile.mainGoal || '—'}
        />
      </div>
    </div>
  );
}
